import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, MapPin, Phone, Rocket, Trash2 } from "lucide-react";
import { apiClient } from "@/lib/auth";

function RequestRow({ r, onDelete }) {
  return (
    <li data-testid={`pilot-request-row-${r.id}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex flex-wrap items-start gap-4">
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-white">{r.full_name} <span className="text-slate-400 font-normal">· {r.role}</span></div>
        <div className="text-sm text-[#F4D88A]">{r.organization}</div>
        {r.share_link && <div data-testid={`pilot-request-via-link-${r.id}`} className="mt-1 inline-block rounded-full border border-brand-cyan/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] text-brand-cyan">via personal link · {r.share_link.director_name}</div>}
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-400">
          <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 hover:text-white"><Mail className="w-3 h-3" />{r.email}</a>
          <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 hover:text-white"><Phone className="w-3 h-3" />{r.phone}</a>
          <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{r.region}</span>
        </div>
      </div>
      <div className="text-right text-xs text-slate-500 font-mono">
        <div>{r.demo_name}</div>
        <div>{new Date(r.created_at).toLocaleString()}</div>
      </div>
      <button type="button" onClick={() => onDelete(r.id)} data-testid={`pilot-request-delete-${r.id}`}
        className="p-2 rounded-lg text-slate-500 hover:text-rose-300 hover:bg-rose-500/10 transition-colors" aria-label="Delete request">
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
}

export default function PilotRequestsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => apiClient.get("/admin/pilot-requests")
    .then(({ data }) => setItems(data?.items || []))
    .catch(() => toast.error("Could not load pilot requests"))
    .finally(() => setLoading(false));
  useEffect(() => { reload(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this pilot request?")) return;
    try { await apiClient.delete(`/admin/pilot-requests/${id}`); toast.success("Request deleted"); reload(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <section data-testid="pilot-requests-page" className="min-h-screen bg-[#0A192F] text-slate-100 py-10 px-6 md:px-10">
      <div className="max-w-5xl mx-auto">
        <Link to="/admin/demos" data-testid="pilot-requests-back"
          className="inline-flex items-center gap-1.5 text-slate-400 hover:text-brand-cyan font-mono text-[11px] tracking-[0.18em] uppercase mb-6 transition">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to demos
        </Link>
        <div className="flex items-center gap-2 text-brand-cyan font-mono text-[11px] tracking-[0.24em] uppercase mb-2">
          <Rocket className="w-3.5 h-3.5" /> // PILOT REQUESTS
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">Pilot requests</h1>
        <p className="text-slate-400 mt-2">Directors who asked for a 90-day pilot from a client demo.</p>
        <div data-testid="pilot-requests-count" className="mt-6 font-mono text-xs text-slate-500">{items.length} request{items.length === 1 ? "" : "s"}</div>
        {loading ? <div className="mt-6 text-slate-500">Loading…</div> : items.length === 0 ? (
          <div data-testid="pilot-requests-empty" className="mt-6 rounded-xl border border-dashed border-white/10 p-10 text-center text-slate-500">No pilot requests yet.</div>
        ) : (
          <ul className="mt-3 space-y-3">{items.map((r) => <RequestRow key={r.id} r={r} onDelete={remove} />)}</ul>
        )}
      </div>
    </section>
  );
}
